import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ComboboxOption } from '../../components/Combobox';
import { Combobox } from '../../components/Combobox';
import { MultiCombobox } from '../../components/MultiCombobox';
import { SuggestInput } from '../../components/SuggestInput';
import { useDebounced } from '../../lib/useDebounced';
import { getContact, getCustomer } from '../crm/api';
import { CustomerCombobox } from '../crm/CustomerCombobox';
import { EmailCombobox } from '../crm/EmailCombobox';
import { useContactLookup } from '../crm/queries';
import { useCategories, useMasterData } from '../masterdata/queries';
import { flattenCategories } from '../tickets/presentation';
import { useAssignableUsers } from '../users/queries';
import { formatConditionValue, NUMERIC_OPERATORS } from './conditions';
import { valueKindOf, type ConditionValue, type ValueKind } from './conditionValues';

interface ConditionValueInputProps {
  field: string;
  op: string;
  value: ConditionValue | null;
  onChange: (value: ConditionValue | null) => void;
}

/**
 * Das Wertefeld einer Bedingung -- passend zum gewaehlten Feld.
 *
 * **Vorher ein Textfeld fuer alles.** Wer eine Regel "Kunde ist Praxis Dr.
 * Beispiel" bauen wollte, musste unter ``customer_id`` die UUID abschreiben;
 * unter ``is_major_incident`` ergab "false" als Text eine Bedingung, die
 * **immer** zutraf, weil der Server den nicht leeren Text als wahr las; und
 * ``in`` verlangt eine Liste, die das Textfeld nie schickte. Jetzt waehlt
 * man, was das Feld meint: einen Kunden, einen Bearbeiter, Ja oder Nein.
 *
 * **Gespeichert wird weiter, was der Server vergleicht** -- bei ``queue_id``
 * die Kennung, bei ``queue.name`` der Name. Die Anzeige in der Liste loest
 * beides wieder in Namen auf (``ConditionValueText``).
 *
 * Textvergleiche (enthaelt, beginnt mit ...) auf einem Namensfeld bleiben
 * Freitext -- mit den vorhandenen Namen als Vorschlag.
 */
export function ConditionValueInput({ field, op, value, onChange }: ConditionValueInputProps) {
  const { t } = useTranslation();
  const kind = valueKindOf(field);
  const label = t('automation.conditionValue');

  if (NUMERIC_OPERATORS.has(op) || kind.kind === 'number') {
    return (
      <input
        className="field__control"
        aria-label={label}
        placeholder={t('automation.numberHint')}
        inputMode="decimal"
        value={typeof value === 'number' ? String(value) : typeof value === 'string' ? value : ''}
        onChange={(event) => {
          const text = event.target.value.replace(',', '.');
          onChange(text.trim() === '' ? null : Number(text));
        }}
      />
    );
  }

  const exact = op === 'equals' || op === 'not_equals';
  const list = op === 'in';

  if (kind.kind === 'boolean') {
    return (
      <PickOne
        label={label}
        value={typeof value === 'boolean' ? String(value) : ''}
        options={[
          { id: 'true', label: t('masterdata.yes') },
          { id: 'false', label: t('masterdata.no') },
        ]}
        onChange={(next) => {
          onChange(next === '' ? null : next === 'true');
        }}
      />
    );
  }

  if (kind.kind === 'customer' && !list) {
    return <CustomerValue kind={kind} label={label} onChange={onChange} exact={exact} />;
  }
  if (kind.kind === 'contact' && exact) {
    return <ContactValue label={label} onChange={onChange} />;
  }
  if (kind.kind === 'email' && exact) {
    return (
      <EmailCombobox
        ariaLabel={label}
        value={typeof value === 'string' ? value : ''}
        onChange={(next) => {
          onChange(next === '' ? null : next);
        }}
      />
    );
  }

  return (
    <ListValue
      kind={kind}
      label={label}
      exact={exact}
      list={list}
      value={value}
      onChange={onChange}
    />
  );
}

/** Stammdaten, Kategorien, Bearbeiter, Aufzaehlungen -- und der Rest als Text. */
function ListValue({
  kind,
  label,
  exact,
  list,
  value,
  onChange,
}: {
  kind: ValueKind;
  label: string;
  exact: boolean;
  list: boolean;
  value: ConditionValue | null;
  onChange: (value: ConditionValue | null) => void;
}) {
  const options = useOptions(kind);

  if (options !== null && list) {
    return (
      <MultiCombobox
        ariaLabel={label}
        options={options}
        value={Array.isArray(value) ? value : []}
        onChange={(next) => {
          onChange(next.length === 0 ? null : next);
        }}
      />
    );
  }
  if (options !== null && exact) {
    return (
      <PickOne
        label={label}
        options={options}
        value={typeof value === 'string' ? value : ''}
        onChange={(next) => {
          onChange(next === '' ? null : next);
        }}
      />
    );
  }

  const text = typeof value === 'string' ? value : Array.isArray(value) ? value.join(', ') : '';
  const toValue = (next: string): ConditionValue | null => {
    if (next.trim() === '') return null;
    // ``in`` verlangt eine Liste -- das Textfeld davor schickte einen Text,
    // und der Server wies die Regel ab.
    return list
      ? next
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry !== '')
      : next;
  };

  // Textvergleich auf einem Namensfeld: frei, aber mit den Namen als Vorschlag.
  if (options !== null) {
    return (
      <LocalSuggest
        label={label}
        value={text}
        suggestions={options.map((option) => option.label)}
        onChange={(next) => {
          onChange(toValue(next));
        }}
      />
    );
  }

  return (
    <input
      className="field__control"
      aria-label={label}
      placeholder={list ? label + ' (a, b, c)' : label}
      value={text}
      onChange={(event) => {
        onChange(toValue(event.target.value));
      }}
    />
  );
}

/**
 * Die Eintraege einer Liste -- oder ``null``, wenn das Feld keine hat.
 *
 * Die Kennung eines Eintrags ist, was gespeichert wird: bei ``..._id`` die
 * UUID, bei ``....name`` der Name selbst.
 */
function useOptions(kind: ValueKind): ComboboxOption[] | null {
  const { t } = useTranslation();
  const masterData = useMasterData(kind.kind === 'masterdata' ? kind.source : 'queue');
  const categories = useCategories();
  const users = useAssignableUsers();

  switch (kind.kind) {
    case 'masterdata':
      return (masterData.data ?? []).map((entry) => ({
        id: kind.by === 'id' ? entry.id : entry.name,
        label: entry.name,
      }));
    case 'category': {
      const flat = flattenCategories(categories.data ?? []);
      return flat.map((entry) => ({
        id: kind.by === 'id' ? entry.id : entry.label.replace(/^[\s\u2514]+/, ''),
        label: entry.label,
      }));
    }
    case 'user':
      return (users.data ?? []).map((user) => ({ id: user.id, label: user.full_name }));
    case 'enum':
      return kind.values.map((entry) => ({ id: entry, label: t(`${kind.labelKey}.${entry}`) }));
    default:
      return null;
  }
}

function PickOne({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Combobox
      ariaLabel={label}
      options={options}
      value={options.find((option) => option.id === value) ?? null}
      onChange={(option) => {
        onChange(option?.id ?? '');
      }}
    />
  );
}

function LocalSuggest({
  label,
  value,
  suggestions,
  onChange,
}: {
  label: string;
  value: string;
  suggestions: string[];
  onChange: (value: string) => void;
}) {
  const [term, setTerm] = useState('');
  const needle = term.trim().toLowerCase();
  return (
    <SuggestInput
      id={undefined}
      value={value}
      onChange={onChange}
      suggestions={suggestions.filter((entry) => entry.toLowerCase().includes(needle))}
      onSearch={setTerm}
      placeholder={label}
    />
  );
}

/** Kunde -- gespeichert als Kennung, Name oder Kundennummer, je nach Feld. */
function CustomerValue({
  kind,
  label,
  exact,
  onChange,
}: {
  kind: Extract<ValueKind, { kind: 'customer' }>;
  label: string;
  exact: boolean;
  onChange: (value: ConditionValue | null) => void;
}) {
  const [chosen, setChosen] = useState<ComboboxOption | null>(null);

  if (!exact && kind.by === 'id') {
    // Ein Textvergleich auf einer Kennung ergibt keinen Sinn; ein Textfeld
    // bleibt fuer den, der es trotzdem will.
    return (
      <input
        className="field__control"
        aria-label={label}
        onChange={(event) => {
          onChange(event.target.value.trim() === '' ? null : event.target.value);
        }}
      />
    );
  }

  return (
    <CustomerCombobox
      ariaLabel={label}
      value={chosen}
      onChange={(option) => {
        setChosen(option);
        if (option === null) {
          onChange(null);
          return;
        }
        onChange(
          kind.by === 'id' ? option.id : kind.by === 'number' ? (option.meta ?? '') : option.label,
        );
      }}
    />
  );
}

/** Ansprechpartner -- gesucht ueber Name und Adresse, gespeichert als Kennung. */
function ContactValue({
  label,
  onChange,
}: {
  label: string;
  onChange: (value: ConditionValue | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [opened, setOpened] = useState(false);
  const [chosen, setChosen] = useState<ComboboxOption | null>(null);
  const contacts = useContactLookup(useDebounced(term), opened);

  return (
    <Combobox
      ariaLabel={label}
      value={chosen}
      loading={contacts.isFetching}
      options={(contacts.data ?? []).map((contact) => ({
        id: contact.id,
        label: `${contact.first_name} ${contact.last_name}`.trim(),
        hint: contact.email ?? undefined,
        meta: contact.customer_name,
      }))}
      onSearch={(next) => {
        setOpened(true);
        setTerm(next);
      }}
      onChange={(option) => {
        setChosen(option);
        onChange(option?.id ?? null);
      }}
    />
  );
}

/**
 * Der Wert einer gespeicherten Bedingung -- als Name statt als Kennung.
 *
 * "queue_id ist gleich 01a0..." sagt niemandem etwas. Aufgeloest wird aus den
 * Listen, die ohnehin geladen sind; Kunde und Ansprechpartner werden einzeln
 * nachgefragt. Was sich nicht aufloesen laesst -- ein geloeschter Eintrag --,
 * steht als Wert da, wie er gespeichert ist.
 */
export function ConditionValueText({ field, value }: { field: string; value: unknown }) {
  const { t } = useTranslation();
  const kind = valueKindOf(field);
  const options = useOptions(kind);
  const single = typeof value === 'string' ? value : '';

  const customer = useQuery({
    queryKey: ['crm', 'customer', single],
    queryFn: () => getCustomer(single),
    enabled: kind.kind === 'customer' && kind.by === 'id' && single !== '',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const contact = useQuery({
    queryKey: ['crm', 'contact', single],
    queryFn: () => getContact(single),
    enabled: kind.kind === 'contact' && single !== '',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (typeof value === 'boolean') {
    return <>{t(value ? 'masterdata.yes' : 'masterdata.no')}</>;
  }
  if (customer.data) return <>{customer.data.name}</>;
  if (contact.data) {
    return <>{`${contact.data.first_name} ${contact.data.last_name}`.trim()}</>;
  }
  if (options !== null && kind.kind !== 'category') {
    const labelOf = (entry: unknown) =>
      options.find((option) => option.id === entry)?.label ?? formatConditionValue(entry);
    return <>{Array.isArray(value) ? value.map(labelOf).join(', ') : labelOf(value)}</>;
  }
  if (kind.kind === 'category' && options !== null) {
    const labelOf = (entry: unknown) =>
      options.find((option) => option.id === entry)?.label.trim() ?? formatConditionValue(entry);
    return <>{Array.isArray(value) ? value.map(labelOf).join(', ') : labelOf(value)}</>;
  }
  return <>{formatConditionValue(value)}</>;
}
